import { ArrowLeft, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { AppFrame } from '../components/frame/AppFrame';
import { NotificationList } from '../components/notifications/NotificationList';
import { Alert, buttonClass, PageSpinner } from '../components/ui';
import { ApiError } from '../lib/api';
import { fetchStopLink, stopEmails } from '../lib/notifications';
import { useSession } from '../lib/session';

const settingsLink = (
  <Link
    to="/account/notifications"
    aria-label="Notification settings"
    className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink hover:bg-field"
  >
    <Settings size={20} strokeWidth={1.9} aria-hidden="true" />
  </Link>
);

/** mogzo.com/notifications (M33): everything the bell has, on a phone or from "See all". */
export function Notifications() {
  const { me } = useSession();
  const navigate = useNavigate();
  const { key } = useLocation();
  if (me === undefined) return <PageSpinner />;
  if (me === null) return <Navigate to="/sign-in?next=/notifications" replace />;
  const back = () => (key === 'default' ? navigate('/') : navigate(-1));
  return (
    <AppFrame
      header={
        <>
          <button
            type="button"
            aria-label="Back"
            onClick={back}
            className="flex size-11 shrink-0 items-center justify-center rounded-full"
          >
            <ArrowLeft size={22} strokeWidth={1.9} aria-hidden="true" />
          </button>
          <span className="min-w-0 flex-1 truncate text-[17px] font-bold">Notifications</span>
          {settingsLink}
        </>
      }
    >
      <main className="mx-auto w-full max-w-[680px] pt-3 pb-10 lg:px-8 lg:pt-8">
        <div className="mb-4 hidden items-center justify-between gap-3 lg:flex">
          <h1 className="text-[28px] leading-[34px] font-bold">Notifications</h1>
          {settingsLink}
        </div>
        <h1 className="sr-only lg:hidden">Notifications</h1>
        <div className="lg:rounded-[20px] lg:border lg:border-line lg:bg-card lg:pt-4">
          <NotificationList />
        </div>
      </main>
    </AppFrame>
  );
}

type Stop =
  | { status: 'loading' }
  | { status: 'bad' }
  | { status: 'ready'; game: string | null }
  | { status: 'done'; game: string | null };

/**
 * mogzo.com/notifications/stop?t=…: where an email's "Stop emails" link opens. It asks first,
 * so a mail app checking links can't stop them by accident. Works signed out.
 */
export function StopEmails() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [state, setState] = useState<Stop>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) return setState({ status: 'bad' });
    fetchStopLink(token)
      .then(({ game }) => setState({ status: 'ready', game }))
      .catch((failure) =>
        setState(
          failure instanceof ApiError && failure.status === 404
            ? { status: 'bad' }
            : { status: 'ready', game: null },
        ),
      );
  }, [token]);

  async function stop() {
    setBusy(true);
    setError(false);
    try {
      const { game } = await stopEmails(token);
      setState({ status: 'done', game });
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const what = (game: string | null) => (game ? `emails for ${game}` : 'all game emails');
  return (
    <AppFrame>
      <main className="mx-auto w-full max-w-[520px] px-4 py-12 sm:px-6">
        <div className="rounded-3xl border border-line bg-card p-7">
          {state.status === 'loading' && (
            <p role="status" className="text-muted">
              Loading…
            </p>
          )}
          {state.status === 'bad' && (
            <>
              <h1 className="text-2xl font-bold">This link doesn’t work</h1>
              <p className="mt-3 leading-relaxed text-muted">
                Change your emails under{' '}
                <Link to="/account/notifications" className="font-semibold text-ink underline">
                  notification settings
                </Link>{' '}
                in your account.
              </p>
            </>
          )}
          {state.status === 'ready' && (
            <>
              <h1 className="text-2xl font-bold">Stop {what(state.game)}?</h1>
              <p className="mt-3 leading-relaxed text-muted">
                You’ll still see your turns on the bell at MOGZO.
              </p>
              {error && (
                <div className="mt-4">
                  <Alert>Couldn’t stop them. Try again in a moment.</Alert>
                </div>
              )}
              <button
                type="button"
                onClick={stop}
                disabled={busy}
                className={`mt-6 ${buttonClass('primary')}`}
              >
                {busy ? 'Stopping…' : 'Stop emails'}
              </button>
            </>
          )}
          {state.status === 'done' && (
            <>
              <h1 className="text-2xl font-bold">Done</h1>
              <p className="mt-3 leading-relaxed text-muted">
                We won’t send you {what(state.game)} any more. You can turn them back on in your{' '}
                <Link to="/account/notifications" className="font-semibold text-ink underline">
                  notification settings
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </main>
    </AppFrame>
  );
}
